// src/pages/hr/recruitment/JobOpenings.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { createJobOpening, updateJobOpening } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";

export default function JobOpenings() {
  const { jobOpenings, jobsLoading } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    department: "Engineering",
    vacancies: 1,
    location: "On-site",
    employmentType: "Full-Time",
    experienceRequired: "2-4 years",
    minSalary: "",
    maxSalary: "",
    description: "",
    skillsRequired: "",
    deadline: "",
    priority: "Medium",
    hiringManager: "",
    recruiter: userProfile?.displayName || "HR Admin",
    status: "Open",
  });

  const depts = [...new Set(jobOpenings.map((j) => j.department).filter(Boolean))];

  const filteredJobs = jobOpenings.filter((j) => {
    const matchSearch =
      (j.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (j.department || "").toLowerCase().includes(search.toLowerCase()) ||
      (j.id || "").toLowerCase().includes(search.toLowerCase());
    const matchDept = filterDept === "all" || j.department === filterDept;
    const matchStatus = filterStatus === "all" || j.status === filterStatus;
    return matchSearch && matchDept && matchStatus;
  });

  function handleOpenCreate() {
    setEditingJob(null);
    setFormData({
      title: "",
      department: "Engineering",
      vacancies: 1,
      location: "On-site",
      employmentType: "Full-Time",
      experienceRequired: "2-4 years",
      minSalary: "",
      maxSalary: "",
      description: "",
      skillsRequired: "",
      deadline: "",
      priority: "Medium",
      hiringManager: "",
      recruiter: userProfile?.displayName || "HR Admin",
      status: "Open",
    });
    setShowModal(true);
  }

  function handleOpenEdit(job) {
    setEditingJob(job);
    setFormData({
      title: job.title || "",
      department: job.department || "Engineering",
      vacancies: job.vacancies || 1,
      location: job.location || "On-site",
      employmentType: job.employmentType || "Full-Time",
      experienceRequired: job.experienceRequired || "2-4 years",
      minSalary: job.minSalary || "",
      maxSalary: job.maxSalary || "",
      description: job.description || "",
      skillsRequired: Array.isArray(job.skillsRequired) ? job.skillsRequired.join(", ") : job.skillsRequired || "",
      deadline: job.deadline || "",
      priority: job.priority || "Medium",
      hiringManager: job.hiringManager || "",
      recruiter: job.recruiter || userProfile?.displayName || "HR Admin",
      status: job.status || "Open",
    });
    setShowModal(true);
  }

  async function handleDuplicate(job) {
    const newJobData = {
      ...job,
      title: `${job.title} (Copy)`,
      status: "Open",
      createdByName: userProfile?.displayName || "HR",
    };
    delete newJobData.id;
    await createJobOpening(newJobData);
  }

  async function handleCloseJob(id) {
    if (window.confirm("Are you sure you want to close this job opening?")) {
      await updateJobOpening(id, { status: "Closed" });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      ...formData,
      vacancies: Number(formData.vacancies),
      skillsRequired: typeof formData.skillsRequired === "string"
        ? formData.skillsRequired.split(",").map((s) => s.trim()).filter(Boolean)
        : formData.skillsRequired,
      createdByName: userProfile?.displayName || "HR",
    };

    if (editingJob) {
      await updateJobOpening(editingJob.id, payload);
    } else {
      await createJobOpening(payload);
    }
    setShowModal(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .ats-toolbar { display: flex; gap: 0.75rem; flex-wrap: wrap; justify-content: space-between; align-items: center; }
        .ats-filter-group { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .ats-input, .ats-select { padding: 0.5rem 0.85rem; border: 1px solid var(--border, #d1d5db); border-radius: 8px; font-size: 0.84rem; background: var(--bg-card, #fff); color: var(--text-primary, #111); outline: none; }
        .ats-input:focus, .ats-select:focus { border-color: #1b4b31; box-shadow: 0 0 0 3px rgba(27,75,49,0.1); }
        .job-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1.25rem; }
        .enterprise-job-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 6px rgba(0,0,0,0.05); display: flex; flexDirection: column; justify-content: space-between; gap: 1rem; transition: transform 0.2s, box-shadow 0.2s; }
        .enterprise-job-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .job-card-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; }
        .job-id-pill { font-family: monospace; font-size: 0.7rem; font-weight: 700; color: var(--text-muted); background: var(--bg-secondary, #f3f4f6); padding: 0.15rem 0.45rem; border-radius: 4px; }
        .job-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #111); margin-top: 4px; }
        .job-meta-tag { font-size: 0.75rem; color: var(--text-muted); display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 6px; }
        .job-status-pill { padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; }
        .job-status--Open { background: #d1fae5; color: #065f46; }
        .job-status--Closed { background: #fee2e2; color: #991b1b; }
        .job-status--Paused { background: #fef3c7; color: #92400e; }
        .job-status--Draft { background: #f3f4f6; color: #6b7280; }
        .job-stats-row { display: flex; gap: 1.5rem; border-top: 1px solid var(--border, #f3f4f6); border-bottom: 1px solid var(--border, #f3f4f6); padding: 0.6rem 0; font-size: 0.8rem; }
        .job-stat-item { display: flex; flex-direction: column; }
        .job-stat-num { font-weight: 700; font-size: 0.95rem; color: #1b4b31; }
        .job-stat-lbl { font-size: 0.7rem; color: var(--text-muted); }
        .job-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; }
        .modal-body { background: var(--bg-card, #fff); border-radius: 12px; width: 100%; max-width: 650px; max-height: 90vh; overflow-y: auto; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; }
        @media(max-width: 600px) { .form-grid-2 { grid-template-columns: 1fr; } }
      `}</style>

      {/* Toolbar */}
      <div className="ats-toolbar">
        <input
          type="text"
          className="ats-input"
          placeholder="🔍 Search job title, ID, department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <div className="ats-filter-group">
          <select className="ats-select" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
            <option value="all">All Departments</option>
            {depts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select className="ats-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Paused">Paused</option>
            <option value="Closed">Closed</option>
          </select>
          <button className="btn-primary" onClick={handleOpenCreate}>
            + Create Job Opening
          </button>
        </div>
      </div>

      {/* Job Cards */}
      {jobsLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Job Openings...</div>
      ) : filteredJobs.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No job openings found matching criteria.
        </div>
      ) : (
        <div className="job-card-grid">
          {filteredJobs.map((job) => (
            <div key={job.id} className="enterprise-job-card">
              <div>
                <div className="job-card-header">
                  <div>
                    <span className="job-id-pill">JOB-{job.id.slice(0, 6).toUpperCase()}</span>
                    <div className="job-title">{job.title}</div>
                  </div>
                  <span className={`job-status-pill job-status--${job.status}`}>{job.status}</span>
                </div>
                <div className="job-meta-tag">
                  <span>🏢 {job.department}</span>
                  <span>📍 {job.location || "On-site"}</span>
                  <span>💼 {job.employmentType}</span>
                  {job.priority && (
                    <span style={{ fontWeight: 700, color: job.priority === "High" ? "#ef4444" : "#f59e0b" }}>
                      ⚡ {job.priority} Priority
                    </span>
                  )}
                </div>
              </div>

              <div className="job-stats-row">
                <div className="job-stat-item">
                  <span className="job-stat-num">{job.applicantCount || 0}</span>
                  <span className="job-stat-lbl">Applicants</span>
                </div>
                <div className="job-stat-item">
                  <span className="job-stat-num">{job.vacancies || 1}</span>
                  <span className="job-stat-lbl">Vacancies</span>
                </div>
                <div className="job-stat-item">
                  <span className="job-stat-num" style={{ fontSize: "0.8rem", color: "var(--text-primary)" }}>
                    {job.hiringManager || "Unassigned"}
                  </span>
                  <span className="job-stat-lbl">Hiring Manager</span>
                </div>
              </div>

              <div className="job-actions">
                <button className="btn-secondary" style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }} onClick={() => handleOpenEdit(job)}>
                  ✏️ Edit
                </button>
                <button className="btn-secondary" style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }} onClick={() => handleDuplicate(job)}>
                  📋 Duplicate
                </button>
                {job.status === "Open" && (
                  <button className="btn-secondary" style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem", color: "#ef4444" }} onClick={() => handleCloseJob(job.id)}>
                    🔒 Close
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-body">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>
                {editingJob ? "Edit Job Opening" : "Create New Job Opening"}
              </h3>
              <button className="btn-secondary" style={{ padding: "0.2rem 0.5rem" }} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Job Title *</label>
                  <input
                    type="text"
                    className="ats-input"
                    style={{ width: "100%" }}
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Department *</label>
                  <input
                    type="text"
                    className="ats-input"
                    style={{ width: "100%" }}
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Vacancies</label>
                  <input
                    type="number"
                    min="1"
                    className="ats-input"
                    style={{ width: "100%" }}
                    value={formData.vacancies}
                    onChange={(e) => setFormData({ ...formData, vacancies: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Employment Type</label>
                  <select
                    className="ats-select"
                    style={{ width: "100%" }}
                    value={formData.employmentType}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                  >
                    <option value="Full-Time">Full-Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Location</label>
                  <select
                    className="ats-select"
                    style={{ width: "100%" }}
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  >
                    <option value="On-site">On-site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Priority</label>
                  <select
                    className="ats-select"
                    style={{ width: "100%" }}
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Hiring Manager</label>
                  <input
                    type="text"
                    className="ats-input"
                    style={{ width: "100%" }}
                    placeholder="e.g. Lead Name"
                    value={formData.hiringManager}
                    onChange={(e) => setFormData({ ...formData, hiringManager: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Deadline</label>
                  <input
                    type="date"
                    className="ats-input"
                    style={{ width: "100%" }}
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Required Skills (comma separated)</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="React, Node.js, TypeScript, SQL"
                  value={formData.skillsRequired}
                  onChange={(e) => setFormData({ ...formData, skillsRequired: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Job Description</label>
                <textarea
                  className="ats-input"
                  style={{ width: "100%", height: 80, resize: "vertical" }}
                  placeholder="Responsibilities, requirements, and job summary..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {editingJob ? "Save Changes" : "Publish Job Opening"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
